import { api } from "@/axios";
import Vue from "vue";
import { permissionsMiddleWare } from "../../../utils/permissionsMiddleWare";
import { getCache, setCache, deleteCacheByPrefix } from "@/utils/cacheDB";

const state = {
  doctors: [],
  lookups: [],
  lookupDetails: {},
  company: [],
  refferedBy: [],
  refferedByHospitals: [],
  discountList: [],
  financeList: []
};

const mutations = {
  set_doctorslist(state, val) {
    state.doctors = val;
  },
  set_refferedby(state, val) {
    state.refferedBy = val;
  },
  set_refferedby_hospitals(state, val) {
    state.refferedByHospitals = val;
  },
  lookup_types(state, val) {
    state.lookups = val;
  },
  set_lookup(state, val) {
    state.lookupDetails = val;
  },
  set_company(state, val) {
    state.company = val;
  },
  set_discountList(state, val) {
    state.discountList = val;
  },
  set_financeList(state, val) {
    state.financeList = val;
  }
};

const actions = {
  async doctorList({ commit, rootState }, query) {
    const { isOther } = rootState.auth.user;
    const queryParams = {
      query: query,
      isOther: isOther
    };
    if (permissionsMiddleWare("List Lookups")) {
      try {
        commit("set_loading", true, { root: true });
        const response = await api.get("/lookup", {
          params: { ...queryParams }
        });
        if (query === "finance_divident") {
          commit("set_financeList", response.data.result);
        } else {
          commit("set_doctorslist", response.data.result);
        }
        commit("set_loading", false, { root: true });
      } catch (error) {
        commit("set_loading", false, { root: true });
      }
    } else {
      Vue.toasted.info(
        "This action cannot be performed by you due to insufficient authorization. Action : List Lookups",
        { duration: 3000 }
      );
    }
  },
  async listRefferedByDoctors({ commit }, query) {
    if (!query) return;
    const mutation =
      query === "hospital" ? "set_refferedby_hospitals" : "set_refferedby";
    // Cache-first: data lives in IndexedDB until logout — no API call
    // when the cache is populated
    const cacheKey = `lookup:${query}`;
    const cached = await getCache(cacheKey);
    if (cached && cached.length > 0) {
      commit(mutation, cached);
      return;
    }
    commit("set_loading", true, { root: true });
    try {
      const response = await api.get(`/lookup?query=${query}`);
      commit(mutation, response.data.result);
      await setCache(cacheKey, response.data.result);
    } catch (error) {
      // toast handled by the axios interceptor
    } finally {
      commit("set_loading", false, { root: true });
    }
  },
  async companyList({ commit }, query) {
    const response = await api.get(`/lookup?query=${query}`);
    commit("set_company", response.data.result);
  },
  async getDiscountList({ commit }) {
    const response = await api.get(`/lookup?query=panel`);
    commit("set_discountList", response.data.result);
  },
  async addDoctors({ commit, dispatch }, payload) {
    try {
      if (permissionsMiddleWare("Create Lookup")) {
        commit("set_loading", true, { root: true });
        await api.post("/lookup", payload);
        await deleteCacheByPrefix("lookup:");
        await dispatch("doctorList");
        Vue.toasted.success("Lookup added successfully").goAway("3000");
        commit("set_loading", false, { root: true });
      } else {
        Vue.toasted.info(
          "This action cannot be performed by you due to insufficient authorization. Action : Create Lookup",
          { duration: 3000 }
        );
      }
    } catch (error) {
      commit("set_loading", false, { root: true });
    }
  },
  async updateDoctor({ commit, dispatch }, { id, payload }) {
    try {
      if (permissionsMiddleWare("Update Lookup")) {
        commit("set_loading", true, { root: true });
        await api.put(`/lookup/${id}`, payload);
        await deleteCacheByPrefix("lookup:");
        await dispatch("doctorList");
        Vue.toasted.success("Lookups updated successfully").goAway("3000");
        commit("set_loading", false, { root: true });
      } else {
        Vue.toasted.info(
          "This action cannot be performed by you due to insufficient authorization. Action : Update Lookup",
          { duration: 3000 }
        );
      }
    } catch (error) {
      commit("set_loading", false, { root: true });
    }
  },
  async deleteDoctor({ dispatch, commit }, id) {
    try {
      if (permissionsMiddleWare("Delete Lookup")) {
        commit("set_loading", true, { root: true });
        await api.delete(`/lookup/${id}`);
        await deleteCacheByPrefix("lookup:");
        await dispatch("doctorList");
        Vue.toasted.success(`Lookup deleted successfully`).goAway("3000");
        commit("set_loading", false, { root: true });
      } else {
        Vue.toasted.info(
          "This action cannot be performed by you due to insufficient authorization. Action : Delete Lookup",
          { duration: 3000 }
        );
      }
    } catch (error) {
      commit("set_loading", false, { root: true });
    }
  },
  async lookupsTypes({ commit }) {
    try {
      if (permissionsMiddleWare("List Lookup Type")) {
        commit("set_loading", true, { root: true });
        const result = await api.get(`/lookup/types`);
        commit("lookup_types", result.data.result);
        commit("set_loading", false, { root: true });
      } else {
        Vue.toasted.info(
          "This action cannot be performed by you due to insufficient authorization. Action : List Lookup Type",
          { duration: 3000 }
        );
      }
    } catch (error) {
      commit("set_loading", false, { root: true });
    }
  },
  async lookupView({ commit }, id) {
    try {
      if (permissionsMiddleWare("View Lookup")) {
        commit("set_loading", true, { root: true });
        const res = await api.get(`/lookup/${id}`);
        commit("set_lookup", res.data.result);
        commit("set_loading", false, { root: true });
      } else {
        Vue.toasted.info(
          "This action cannot be performed by you due to insufficient authorization. Action : View Lookup",
          { duration: 3000 }
        );
      }
    } catch (error) {
      commit("set_loading", false, { root: true });
    }
  }
};

const getters = {
  listDoctors: state => state.doctors,
  filterLookups: state => state.lookups,
  discountList: state => state.discountList,
  companyName: state => state.company || [],
  lookupDetails: state => (state.lookupDetails ? state.lookupDetails : {}),
  refferedByDoctors: state => {
    const doctors = state.refferedBy.map(item => ({
      id: item.id,
      name: item && item.name
    }));
    const hospitals = state.refferedByHospitals.map(item => ({
      id: item.id,
      name: item && item.name
    }));
    return [...doctors, ...hospitals];
  },
  financeList: state => state.financeList
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
};
